import React from 'react';
import { AnalysisData, OffreData } from '../../types/index';
import { StackedBar } from './StackedBar';

interface FinancingStructureProps {
  analysis: AnalysisData;
  offreData: OffreData;
}

const fmt = (v: number) =>
  new Intl.NumberFormat('fr-CH', { maximumFractionDigits: 0 }).format(v);

interface FinancingColData {
  prixTotal: number;
  pretHypothecaire: number;
  apport: number;
  fraisAcquisition: number;
  fondsPropreNecessaire: number;
}

const FinancingColumn: React.FC<{ label: string; badge: string; badgeColor: string; data: FinancingColData }> = ({
  label,
  badge,
  badgeColor,
  data,
}) => {
  const segments = [
    { label: 'Prêt hypothécaire', value: data.pretHypothecaire, color: '#7C3AED' },
    { label: 'Apport', value: data.apport, color: '#10B981' },
    { label: "Frais d'acquisition", value: data.fraisAcquisition, color: '#9CA3AF' },
  ].filter(s => s.value > 0);

  const total = data.prixTotal;
  const segmentsWithPct = segments.map(s => ({
    ...s,
    percentage: total > 0 ? (s.value / total) * 100 : 0,
  }));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      {/* Badge */}
      <div className="flex items-center gap-2 mb-4">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badgeColor}`}>
          {badge}
        </span>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</h4>
      </div>

      <StackedBar
        segments={segmentsWithPct}
        total={total}
        totalLabel="Total"
      />

      {/* Détails */}
      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">Prix total (avec frais)</span>
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{fmt(data.prixTotal)} CHF</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">Prêt hypothécaire</span>
          <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">{fmt(data.pretHypothecaire)} CHF</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">Apport</span>
          <span className="text-sm font-semibold text-green-600 dark:text-green-400">{fmt(data.apport)} CHF</span>
        </div>
        {data.fondsPropreNecessaire > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
            <span className="text-xs text-gray-500 dark:text-gray-400">Fonds propres nécessaires</span>
            <span className="text-sm font-bold text-green-600 dark:text-green-400">{fmt(data.fondsPropreNecessaire)} CHF</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const FinancingStructure: React.FC<FinancingStructureProps> = ({ analysis, offreData }) => {
  const prixData: FinancingColData = {
    prixTotal: analysis.prixTotal,
    pretHypothecaire: analysis.pretHypothecaire,
    apport: analysis.apport,
    fraisAcquisition: analysis.fraisAcquisition,
    fondsPropreNecessaire: analysis.fondsPropreNecessaire,
  };

  const conseilleData: FinancingColData = {
    prixTotal: offreData.prixTotal,
    pretHypothecaire: offreData.pretHypothecaire,
    apport: offreData.fondsPropreNecessaire,
    fraisAcquisition: offreData.fraisAcquisition,
    fondsPropreNecessaire: offreData.fondsPropreNecessaire,
  };

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">
        Structure de financement
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FinancingColumn
          label="Structure de financement"
          badge="Prix affiché"
          badgeColor="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
          data={prixData}
        />
        <FinancingColumn
          label="Structure de financement"
          badge="Offre conseillée"
          badgeColor="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400"
          data={conseilleData}
        />
      </div>
    </div>
  );
};
